import * as m from '@/paraglide/messages';
import { Schema } from 'effect';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import IconHuman from '@listening/assets/icons/icon-human.svg?react';
import IconHumanWhite from '@listening/assets/icons/icon-human-white.svg?react';
import ProfileIcon from '@listening/assets/icons/profile.svg?react';
import { cn } from '@listening/utils/react-helpers';
import { effectQueryErrorMessage } from '@listening/frontend/query/query-error';
import {
  Dialog,
  DialogTrigger,
} from '@listening/frontend/components/radix/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@listening/frontend/components/radix/form';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import type { Control, Resolver } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { ErrorComponent } from '@/components/shared-ui/error-component';
import { SectionLabel } from '@/components/web-fe/design-v2/section-label';
import { SettingsCard } from '@/components/web-fe/design-v2/settings-shell';
import {
  fieldSurface,
  primaryButtonSurface,
  secondaryButtonSurface,
  wellSurface,
} from '@/components/web-fe/design-v2/surfaces';
import { DeleteUserDialog } from '@/components/web-fe/dialogs/cancel-delete-account-flow/delete-user-dialog';
import UserAvatar from '@/components/web-fe/misc/user-avatar';
import { DashboardPage } from '@/components/web-fe/pages/dashboard/dashboard-page';
import { LinkSocialLoginsCard } from '@/components/web-fe/settings/link-social-logins-card';
import { rootLogger } from '@/lib/root-logger';
import { useAnalytics } from '@listening/frontend/analytics/client';
import type { UpdateUserProfilePayload } from '@listening/frontend/query/user';
import { useUserSessionInfo } from '@/services/user-service';
import {
  usePatchUserProfileMutation,
  useUserProfileQuery,
} from '@listening/frontend/query/user-hooks';

const moduleLogger = rootLogger.child({ module: 'ProfileRoute' });

export const Route = createFileRoute('/app/_authenticated/_dashboard/profile')({
  component: ProfilePage,
  context: () => ({
    title: m.common_profile(),
    icon: ProfileIcon,
  }),
});

const ProfileIconComponent = ({ className }: { className?: string }) => (
  <>
    <div className='dark:hidden'>
      <IconHuman className={className} />
    </div>
    <div className='hidden dark:block'>
      <IconHumanWhite className={className} />
    </div>
  </>
);

const actionButton =
  'flex h-9 items-center gap-1.5 px-3.5 text-[13px] font-medium';

function ProfilePage() {
  const userProfileQuery = useUserProfileQuery();

  if (userProfileQuery.isPending) return <ProfilePageSkeleton />;
  if (userProfileQuery.isError)
    return (
      <ErrorComponent
        header={
          effectQueryErrorMessage(userProfileQuery.error) ??
          m.profile_error_fetching()
        }
      />
    );

  const userProfile = userProfileQuery.data;

  return (
    <DashboardPage
      header={{
        Icon: ProfileIconComponent,
        title: m.common_account_information(),
      }}
    >
      <LoadedProfilePage userProfile={userProfile} />
    </DashboardPage>
  );
}

const formSchema = Schema.Struct({
  first_name: Schema.optional(Schema.String.check(Schema.isMaxLength(80))),
  last_name: Schema.optional(Schema.String.check(Schema.isMaxLength(80))),
});
type FormValues = typeof formSchema.Type;

type UserProfileView = {
  first_name: string | null;
  last_name: string | null;
};

function LoadedProfilePage({ userProfile }: { userProfile: UserProfileView }) {
  const form = useForm<FormValues>({
    resolver: standardSchemaResolver(
      Schema.toStandardSchemaV1(formSchema),
    ) as Resolver<FormValues>,
    defaultValues: {
      first_name: userProfile.first_name ?? '',
      last_name: userProfile.last_name ?? '',
    },
  });

  const sessionInfo = useUserSessionInfo();
  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dialogKey, setDialogKey] = useState(0);
  const { capture } = useAnalytics();

  useEffect(() => {
    form.reset({
      first_name: userProfile.first_name ?? '',
      last_name: userProfile.last_name ?? '',
    });
  }, [form, userProfile.first_name, userProfile.last_name]);

  const patchUserProfileMutation = usePatchUserProfileMutation();
  function onSubmit(values: FormValues) {
    const dtoWithNulls = {
      first_name: values.first_name?.trim(),
      last_name: values.last_name?.trim(),
    };
    const truthyDto = Object.fromEntries(
      Object.entries(dtoWithNulls).filter(([, v]) => v),
    ) as Partial<UpdateUserProfilePayload>;

    const truthyUserProfile = Object.fromEntries(
      Object.entries({
        first_name: userProfile.first_name?.trim(),
        last_name: userProfile.last_name?.trim(),
      }).filter(([, v]) => v),
    );

    if (
      Object.keys(truthyDto).length === 0 ||
      JSON.stringify(truthyDto) === JSON.stringify(truthyUserProfile)
    ) {
      toast.info(m.profile_no_changes());
      return;
    }

    patchUserProfileMutation.mutate(truthyDto, {
      onError: (error) => {
        moduleLogger.error({ error }, 'Patch user profile request failed');
        toast.error(m.profile_save_failed());
      },
      onSuccess: () => {
        moduleLogger.info('Patch user profile successful');
        toast.success(m.profile_saved());
        setIsEditing(false);
        form.reset({
          first_name: values.first_name ?? '',
          last_name: values.last_name ?? '',
        });
      },
    });
  }

  const displayName = useMemo(() => {
    const first = userProfile.first_name?.trim();
    const last = userProfile.last_name?.trim();
    if (first || last) return [first, last].filter(Boolean).join(' ');
  }, [userProfile.first_name, userProfile.last_name]);

  const handleCancelEdit = () => {
    form.reset({
      first_name: userProfile.first_name ?? '',
      last_name: userProfile.last_name ?? '',
    });
    setIsEditing(false);
  };

  return (
    <div className='flex flex-col gap-[var(--v2-gap-6)]'>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <SettingsCard>
            <div className='flex flex-col gap-[var(--v2-gap-4)] p-[var(--v2-gap-4)]'>
              <div className='flex flex-wrap items-center justify-between gap-[var(--v2-gap-4)]'>
                <div className='flex items-center gap-[var(--v2-gap-4)]'>
                  <UserAvatar
                    className='size-16 md:size-20'
                    fallbackClassName='text-xl md:text-2xl'
                  />
                  <div className='flex flex-col gap-0.5'>
                    <p
                      className='text-[17px] text-[var(--v2-text-primary)]'
                      style={{ fontWeight: 'var(--v2-heading-weight)' }}
                    >
                      {displayName ??
                        sessionInfo.email ??
                        m.profile_your_account()}
                    </p>
                    <p className='text-[13px] text-[var(--v2-text-muted)]'>
                      {sessionInfo.email ?? m.profile_add_email()}
                    </p>
                  </div>
                </div>
                {!isEditing && (
                  <button
                    type='button'
                    className={cn(secondaryButtonSurface, actionButton)}
                    onClick={() => {
                      setIsEditing(true);
                    }}
                  >
                    {m.common_edit()}
                  </button>
                )}
              </div>

              <div className='grid gap-[var(--v2-gap-3)] sm:grid-cols-2'>
                <NameFieldCard
                  label={m.profile_first_name()}
                  name='first_name'
                  control={form.control}
                  isEditing={isEditing}
                  value={userProfile.first_name}
                />
                <NameFieldCard
                  label={m.profile_last_name()}
                  name='last_name'
                  control={form.control}
                  isEditing={isEditing}
                  value={userProfile.last_name}
                />
              </div>
              {isEditing && (
                <div className='flex flex-wrap justify-end gap-[var(--v2-gap-3)]'>
                  <button
                    type='button'
                    className={cn(secondaryButtonSurface, actionButton)}
                    onClick={handleCancelEdit}
                  >
                    {m.common_cancel()}
                  </button>
                  <button
                    type='submit'
                    className={cn(primaryButtonSurface, actionButton)}
                    disabled={patchUserProfileMutation.isPending}
                  >
                    {patchUserProfileMutation.isPending
                      ? m.profile_saving()
                      : m.profile_save_changes()}
                  </button>
                </div>
              )}
            </div>
          </SettingsCard>
        </form>
      </Form>

      <LinkSocialLoginsCard />

      <SettingsCard>
        <div className='flex flex-col gap-[var(--v2-gap-4)] p-[var(--v2-gap-4)]'>
          <div className='flex flex-col gap-0.5'>
            <p
              className='text-[15px] text-scarlet-400'
              style={{ fontWeight: 'var(--v2-heading-weight)' }}
            >
              {m.profile_danger_zone()}
            </p>
            <p className='text-[13px] text-[var(--v2-text-muted)]'>
              {m.profile_permanently_delete()}
            </p>
          </div>
          <Dialog
            open={deleteDialogOpen}
            onOpenChange={(v) => {
              setDeleteDialogOpen(v);
              setDialogKey((k) => k + 1);
            }}
          >
            <DialogTrigger asChild>
              <button
                type='button'
                onClick={() => {
                  capture('delete_account_clicked', null);
                }}
                className={cn(
                  secondaryButtonSurface,
                  actionButton,
                  'w-fit text-scarlet-400',
                )}
              >
                {m.profile_delete_account()}
              </button>
            </DialogTrigger>
            <DeleteUserDialog
              key={dialogKey}
              closeDialog={() => {
                setDeleteDialogOpen(false);
              }}
            />
          </Dialog>
        </div>
      </SettingsCard>
    </div>
  );
}

function SkeletonBlock({ className }: { className?: string }) {
  return (
    <span
      aria-hidden='true'
      className={cn(
        'block animate-pulse rounded-[6px] bg-[var(--v2-card-bg)]',
        className,
      )}
    />
  );
}

function ProfilePageSkeleton() {
  return (
    <DashboardPage
      header={{
        Icon: ProfileIconComponent,
        title: m.common_account_information(),
      }}
    >
      <div
        aria-busy='true'
        aria-live='polite'
        className='flex flex-col gap-[var(--v2-gap-6)]'
      >
        <SettingsCard>
          <div className='flex flex-col gap-[var(--v2-gap-4)] p-[var(--v2-gap-4)]'>
            <div className='flex items-center gap-[var(--v2-gap-4)]'>
              <SkeletonBlock className='size-16 rounded-full md:size-20' />
              <div className='flex flex-col gap-2'>
                <SkeletonBlock className='h-5 w-40' />
                <SkeletonBlock className='h-4 w-56' />
              </div>
            </div>
            <div className='grid gap-[var(--v2-gap-3)] sm:grid-cols-2'>
              <SkeletonBlock className='h-[76px] rounded-[var(--v2-card-radius)]' />
              <SkeletonBlock className='h-[76px] rounded-[var(--v2-card-radius)]' />
            </div>
          </div>
        </SettingsCard>

        <SettingsCard>
          <div className='flex flex-col gap-[var(--v2-gap-3)] p-[var(--v2-gap-4)]'>
            <SkeletonBlock className='h-5 w-40' />
            <SkeletonBlock className='h-10 w-full' />
            <SkeletonBlock className='h-10 w-full' />
          </div>
        </SettingsCard>

        <SettingsCard>
          <div className='flex flex-col gap-[var(--v2-gap-3)] p-[var(--v2-gap-4)]'>
            <SkeletonBlock className='h-4 w-24' />
            <SkeletonBlock className='h-3 w-64' />
            <SkeletonBlock className='h-9 w-40 rounded-[var(--v2-button-radius)]' />
          </div>
        </SettingsCard>
      </div>
    </DashboardPage>
  );
}

function NameFieldCard({
  label,
  name,
  control,
  isEditing,
  value,
}: {
  label: string;
  name: 'first_name' | 'last_name';
  control: Control<FormValues>;
  isEditing: boolean;
  value?: string | null;
}) {
  return (
    <div className={cn(wellSurface, 'flex flex-col gap-2 p-[var(--v2-gap-3)]')}>
      <SectionLabel>{label}</SectionLabel>
      {isEditing ? (
        <FormField
          control={control}
          name={name}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <input
                  type='text'
                  placeholder={label}
                  maxLength={80}
                  className={cn(
                    fieldSurface,
                    'h-9 w-full px-3 text-sm font-medium outline-none focus:border-[var(--v2-outline-strong)]',
                  )}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : (
        <p
          className='text-[15px] text-[var(--v2-text-primary)]'
          style={{ fontWeight: 'var(--v2-heading-weight)' }}
        >
          {value && value.trim().length > 0 ? value : m.profile_not_set()}
        </p>
      )}
    </div>
  );
}
